import React from 'react';
import { Badge } from 'components/Badge';
import { Button } from 'components/Button';
import { Card } from 'components/Card';
import { Footer } from 'components/Footer';
import { InputFormField } from 'components/InputFormField';
import { Loading } from 'components/Loading';
import { Title } from 'components/Title';
import { PlaceHolderNotExists } from 'components/PlaceholderNotExists';

import { maskCEP, maskCNPJ } from 'utils/masks';
import { Container, CustumerDetailsCard, IntegrationContainer } from './styles';
import { SelectModified } from 'components/SelectModified';
import { Separator } from 'components/Separator';
import { CheckBox, CheckBoxChangeEvent } from 'components/CheckBox';
import { useRegistrationData } from './useRegistrationData';
import { formattingNumberToText } from 'utils/formattingInputs';

const numberRangeToText = (rangeStart:number, rangeEnd:number, currency?:boolean) => {
  return `De ${currency ? 'R\$' : ''}${formattingNumberToText(rangeStart)} a ${currency ? 'R\$' : ''}${formattingNumberToText(rangeEnd)}`;
}

export const RegistrationData: React.FC = () => {
  const { 
    isLoading,
    hasError,
    selectsOptions,
    changeFooter,
    onInputFormFieldChange,
    onSelectChange,
    onCheckBoxChange,
    changeNameTag,
    showAlertForm,
    submitButton,
    handleSubmit,
    inputsValues,
    touched,
    errors,
  } = useRegistrationData();

  if(isLoading) {
    return (
      <Card title="Dados base">
        <Loading />
      </Card>
    )
  }

  if (!hasError) {
    return (
      <form onSubmit={handleSubmit}>
        <Container className="isActive">
          <Card title="Dados base">
            <CustumerDetailsCard>
              <div className="card-body">
                <div className="card-body-wrapper">
                  <InputFormField
                    name="legalName"
                    label="Razão Social"
                    value={inputsValues.legalName}
                    error={touched.legalName && !!errors.legalName}
                    mensageError="Razão social inválida"
                    backGround="white"
                    block={true}
                    marginBotton="0"
                    onChange={onInputFormFieldChange}
                  />
                  <InputFormField
                    name="nickName"
                    label="Nome fantasia"
                    value={inputsValues.nickName}
                    error={touched.nickName && !!errors.nickName}
                    mensageError="Nome fantasia social inválido"
                    backGround="white"
                    block={true}
                    marginBotton="0"
                    onChange={onInputFormFieldChange}
                  />

                  <InputFormField
                    name="ecommerceUrl"
                    label="Site"
                    value={inputsValues.ecommerceUrl}
                    error={touched.ecommerceUrl && !!errors.ecommerceUrl}
                    mensageError="Site inválido"
                    backGround="white"
                    block={true}
                    marginBotton="0"
                    onChange={onInputFormFieldChange}
                  />

                  <div className='ids-container'>
                    <InputFormField
                      name="clearsaleEntityId"
                      label="Clearsale ID"
                      typeInput="number"
                      value={inputsValues.clearsaleEntityId?.toString()}
                      error={touched.clearsaleEntityId && !!errors.clearsaleEntityId}
                      mensageError="Clearsale ID inválido"
                      backGround="white"
                      block={true}
                      readOnly={true}
                      marginBotton="0"
                      onChange={onInputFormFieldChange}
                    />

                    <InputFormField
                      name="afterSaleEcommerceUuid"
                      label="Aftersale ID"
                      value={inputsValues.afterSaleEcommerceUuid ? inputsValues.afterSaleEcommerceUuid?.toString() : ''}
                      error={touched.afterSaleEcommerceUuid && !!errors.afterSaleEcommerceUuid}
                      mensageError="Aftersale ID inválido"
                      backGround="white"
                      block={true}
                      readOnly={(inputsValues.afterSaleEcommerceUuid.length > 35) ? true : false}
                      marginBotton="0"
                      onChange={onInputFormFieldChange}
                    />

                    <InputFormField
                      name="sellerId"
                      label="Confi ID"
                      value={inputsValues?.sellerId?.toString()}
                      error={touched.sellerId && !!errors.sellerId}
                      mensageError="Confi ID inválido"
                      backGround="white"
                      block={true}
                      readOnly={true}
                      marginBotton="0"
                      onChange={onInputFormFieldChange}
                    />

                    <InputFormField
                      name="parentSellerId"
                      label="Seller Pai"
                      typeInput="number"
                      value={inputsValues.parentSellerId !== 0 ? inputsValues.parentSellerId?.toString() : ''}
                      error={touched.parentSellerId && !!errors.parentSellerId}
                      mensageError="Seller Pai inválido"
                      backGround="white"
                      block={true}
                      marginBotton="0"
                      onChange={onInputFormFieldChange}
                    />
                  </div>

                </div>

                <div className="card-body-wrapper">
                  <div className="card-body-input">
                    <SelectModified
                      name="segmentId"
                      label="Categoria"
                      value={inputsValues.segmentId?.toString()}
                      errorMessage="Categoria inválida"
                      error={touched.segmentId && !!errors.segmentId}
                      onChange={onSelectChange}
                      block={true}
                      options={
                        selectsOptions?.segment?.map((contact) => {
                          return {
                            label: contact.segment ?? '',
                            value: contact.segmentId?.toString()
                          }
                        }) ?? []
                      }
                    />
                    <SelectModified
                      name="ecommercePlatformId"
                      label="Plataforma"
                      value={inputsValues.ecommercePlatformId?.toString()}
                      errorMessage="Categoria inválida"
                      error={touched.ecommercePlatformId && !!errors.ecommercePlatformId}
                      onChange={onSelectChange}
                      block={true}
                      options={
                        selectsOptions?.ecommercePlatform?.map((platform) => {
                          return {
                            label: platform.ecommercePlatform ?? '',
                            value: platform.ecommercePlatformId?.toString()
                          }
                        }) ?? []
                      }
                    />
                    <SelectModified
                      name="amountRangeId"
                      label="Quantidade de vendas / mês"
                      value={inputsValues.amountRangeId?.toString()}
                      errorMessage="Quantidade inválida"
                      error={touched.amountRangeId && !!errors.amountRangeId}
                      onChange={onSelectChange}
                      block={true}
                      options={
                        selectsOptions?.amountRange?.map((amountRange) => {
                          return {
                            label: numberRangeToText(amountRange.amountRangeStart, amountRange.amountRangeEnd),
                            value: amountRange.amountRangeId?.toString()
                          }
                        }) ?? []
                      }
                    />
                    <SelectModified
                      name="billingRangeId"
                      label="Faturamento mensal"
                      value={inputsValues.billingRangeId?.toString()}
                      errorMessage="Faturamento inválido"
                      error={touched.billingRangeId && !!errors.billingRangeId}
                      onChange={onSelectChange}
                      block={true}
                      options={
                        selectsOptions?.billingRange?.map((billingRange) => {
                          return {
                            label: numberRangeToText(billingRange.billingRangeStart, billingRange.billingRangeEnd, true),
                            value: billingRange.billingRangeId?.toString()
                          }
                        }) ?? []
                      }
                    />
                  </div>
                </div>

                <Separator maxWidth="100%" marginY="30"/>

                <div className="card-body-wrapper">
                  <div className="card-body-checkbox">
                    <CheckBox
                      name="notificationApp"
                      label="Notificar no app"
                      value="notificationApp"
                      checked={inputsValues.notificationApp}
                      onChange={(event:CheckBoxChangeEvent) => onCheckBoxChange(
                        event,
                        "Parar de Notificar no App?",
                        "Ao confirmar, as transações não serão enviadas para o app Confi",
                      )}
                    />
                    
                    <CheckBox
                      name="notificationEmail"
                      label="Notificar no e-mail"
                      value="notificationEmail"
                      checked={inputsValues.notificationEmail}
                      onChange={(event:CheckBoxChangeEvent) => onCheckBoxChange(
                        event,
                        "Parar de Notificar no e-mail?",
                        "Ao confirmar, as transações não serão enviadas para o e-mail do seller",
                      )}
                    />

                    <CheckBox
                      name="sendSurvey"
                      label="Enviar pesquisa"
                      value="sendSurvey"
                      checked={inputsValues.sendSurvey}
                      onChange={(event:CheckBoxChangeEvent) => onCheckBoxChange(
                        event,
                        "Parar de enviar Survey?",
                        "Ao confirmar, as pesquisas não serão enviadas para o app Confi",
                      )}
                    />
                    <CheckBox
                      name="isConfi"
                      label="Usuário verificado"
                      value="isConfi"
                      checked={inputsValues.isConfi}
                      onChange={(event:CheckBoxChangeEvent) => onCheckBoxChange(
                        event,
                        "Desativar este seller?",
                        "Ao confirmar, notificações e pesquisas serão desligadas, e o seller não irá aparecer para usuários.",
                      )}
                    />
                    <CheckBox
                      readOnly={true}
                      name="integratedSeller"
                      label="Seller integrado"
                      value="integratedSeller"
                      checked={inputsValues.integratedSeller}
                    />
                    <CheckBox
                      name="active"
                      label="Ativo"
                      value="active"
                      checked={inputsValues.active}
                      onChange={(event:CheckBoxChangeEvent) => onCheckBoxChange(
                        event,
                        "Desativar Seller?",
                        "Ao desativar, nenhum registro deste seller será exibido no aplicativo e nem no site. Deseja continuar?",
                      )}
                    />
                  </div>
                </div>

              </div>
            </CustumerDetailsCard>
          </Card>

          <Card title="Integrações">
            <IntegrationContainer>
              <strong>SocialSoul</strong>
                <CheckBox
                  name="monetizedLink"
                  label="Link monetizado"
                  value="monetizedLink"
                  checked={inputsValues.isSocialSoul}
                  readOnly
                />

                <CheckBox
                  name="bid"
                  label="Ofertas"
                  value="bid"
                  checked={false}
                  readOnly
                />
            </IntegrationContainer>

            <Separator maxWidth="100%" marginY="30"/>

            <IntegrationContainer>
              <strong>Aftersale</strong>
              
              <CheckBox
                name="deliveryJourney"
                label="Jornada de entregas"
                value="deliveryJourney"
                checked={inputsValues.deliveryJourney ?? false}
                onChange={(event:CheckBoxChangeEvent) => onCheckBoxChange(
                  event,
                  "Desativar seller como Jornada de Entrega?",
                  "Ao confirmar, estará excluindo o seller no grupo de sellers participantes do JE, tem certeza que deseja confirmar?",
                )}
              />

              <CheckBox
                name="exchangesAndReturns"
                label="Trocas e Devoluções"
                value="exchangesAndReturns"
                checked={inputsValues.exchangesAndReturns ?? false}
                onChange={(event:CheckBoxChangeEvent) => onCheckBoxChange(
                  event,
                  "Desativar seller como Troca & Devoluções?",
                  "Ao confirmar, estará excluindo o seller no grupo de sellers participantes do T&D, tem certeza que deseja confirmar?",
                )}
              />

            </IntegrationContainer>

          </Card>

          <Card title="Dados cadastrais">
            <CustumerDetailsCard>
              <div className="card-body">
                <div className="card-body-wrapper">
                  <InputFormField
                    name="cnpj"
                    label="CNPJ"
                    value={inputsValues.cnpj}
                    error={touched.cnpj && !!errors.cnpj}
                    mensageError="CNPJ inválido"
                    typeInput="text"
                    backGround="white"
                    block={true}
                    mask={maskCNPJ}
                    marginBotton="0"
                    onChange={onInputFormFieldChange}
                  />

                  <InputFormField
                    name="cnpjStatus"
                    label="Status CNPJ"
                    value={inputsValues.cnpjStatus}
                    error={touched.cnpjStatus && !!errors.cnpjStatus}
                    mensageError="CNPJ inválido"
                    typeInput="text"
                    backGround="white"
                    block={true}
                    marginBotton="0"
                    readOnly
                  />

                  <InputFormField
                    name="zipcode"
                    label="CEP"
                    value={inputsValues.zipcode}
                    error={touched.zipcode && !!errors.zipcode}
                    mensageError="CEP não encontrado"
                    typeInput="text"
                    backGround="white"
                    block={true}
                    mask={maskCEP}
                    marginBotton="0"
                    onChange={onInputFormFieldChange}
                  />

                  <InputFormField
                    name="state"
                    label="Estado"
                    value={inputsValues.state}
                    error={touched.state && !!errors.state}
                    mensageError="Estado inválido"
                    typeInput="text"
                    backGround="white"
                    block={true}
                    marginBotton="0"
                    onChange={onInputFormFieldChange}
                  />

                  <InputFormField
                    name="city"
                    label="Cidade"
                    value={inputsValues.city}
                    error={touched.city && !!errors.city}
                    mensageError="Cidade inválida"
                    typeInput="text"
                    backGround="white"
                    block={true}
                    marginBotton="0"
                    onChange={onInputFormFieldChange}
                  />
                </div>

                <div className="card-body-wrapper">
                  <InputFormField
                    name="address"
                    label="Logradouro"
                    value={inputsValues.address}
                    error={touched.address && !!errors.address}
                    mensageError="Logradouro inválido"
                    typeInput="text"
                    backGround="white"
                    block={true}
                    marginBotton="0"
                    onChange={onInputFormFieldChange}
                  />
                  <InputFormField
                    name="district"
                    label="Bairro"
                    value={inputsValues.district}
                    error={touched.district && !!errors.district}
                    mensageError="Bairro inválido"
                    typeInput="text"
                    backGround="white"
                    block={true}
                    marginBotton="0"
                    onChange={onInputFormFieldChange}
                  />

                  <InputFormField
                    name="addressNumber"
                    label="Número"
                    value={inputsValues.addressNumber}
                    error={touched.addressNumber && !!errors.addressNumber}
                    mensageError="Número inválido"
                    typeInput="text"
                    backGround="white"
                    block={true}
                    marginBotton="0"
                    onChange={onInputFormFieldChange}
                  />

                  <InputFormField
                    name="addressComplement"
                    label="Complemento"
                    value={inputsValues.addressComplement}
                    typeInput="text"
                    backGround="white"
                    block={true}
                    marginBotton="0"
                    onChange={onInputFormFieldChange}
                  />
                </div>

              </div>
            </CustumerDetailsCard>
          </Card>
          <Footer>
            <Title elementHTML="strong" text="Campos atualizados:" className="tag" />

            {changeFooter.map((item: string) => (item ? <Badge value={changeNameTag(item) || ""} key={item} /> : null))}
            
            <Button type="button" label="Descartar" size="medium" typeBtn="outline" onClick={() => showAlertForm(false)} />
            <Button type="button" label="Atualizar dados" size="medium" typeBtn="blue" disable={submitButton} onClick={() => showAlertForm(true)} />

          </Footer>
        </Container>
      </form>
    )
  }

  return (
    <Card title="Dados pessoais">
      <PlaceHolderNotExists title="Erro ao tentar conectar com o servidor" />
    </Card>
  );
}